import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, User as UserIcon, Shield, AlertTriangle, Trophy, Settings, Skull } from 'lucide-react';
import { UserProfileTab } from './components/UserProfileTab';
import { UserAccountTab } from './components/UserAccountTab';
import { UserRankingsTab } from './components/UserRankingsTab';
import { UserRiskTab } from './components/UserRiskTab';
import { UserDangerZoneTab } from './components/UserDangerZoneTab';
import { HardDeleteModal } from './components/HardDeleteModal';

// API helper with cookie auth
async function fetchApi<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!res.ok) {
    throw new Error(`Request failed: ${res.status}`);
  }
  return res.json();
}

export interface UserDetail {
  id: number;
  cognitoSub: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string | null;
  profileName: string | null;
  profilePictureUrl: string | null;
  zipCode: string | null;
  sport: string | null;
  visibility: string | null;
  ageRange: string | null;
  gender: string | null;
  handedness: string | null;
  playStyle: string | null;
  backhand: string | null;
  roleCode: string;
  ustaRank: string | null;
  utrRank: string | null;
  pickleballRank: string | null;
  tennisPrimaryLevel: number | null;
  pickleballPrimaryLevel: number | null;
  active: boolean;
  suspended: boolean;
  suspendedAt: string | null;
  suspendedBy: string | null;
  suspendReason: string | null;
  suspendUntil: string | null;
  emailVerified: boolean;
  mobileVerified: boolean;
  ageVerified: boolean;
  onboardingComplete: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  squadsCount: number;
  reportsAgainstCount: number;
  reportsFiledCount: number;
  flaggedMessagesCount: number;
  warningsCount: number;
  riskScore: number | null;
  riskLevel: string | null;
}

const TABS = [
  { id: 'profile', label: 'Profile', icon: UserIcon },
  { id: 'account', label: 'Account', icon: Settings },
  { id: 'rankings', label: 'Rankings', icon: Trophy },
  { id: 'risk', label: 'Risk & Reports', icon: AlertTriangle },
  { id: 'danger', label: 'Danger Zone', icon: Skull },
] as const;

type TabId = typeof TABS[number]['id'];

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [user, setUser] = useState<UserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const tab = searchParams.get('tab');
    if (tab && TABS.some(t => t.id === tab)) return tab as TabId;
    return 'profile';
  });
  const [isEditing, setIsEditing] = useState(() => searchParams.get('edit') === 'true');
  const [isSaving, setIsSaving] = useState(false);
  const [showHardDelete, setShowHardDelete] = useState(false);

  const fetchUser = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchApi<UserDetail>(`/api/v1/admin/users/${id}`);
      setUser(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleSave = async (updates: Partial<UserDetail>) => {
    if (!id) return;
    setIsSaving(true);
    try {
      const data = await fetchApi<UserDetail>(`/api/v1/admin/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      setUser(data);
      setIsEditing(false);
    } catch (err) {
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-slate-700 rounded mb-4" />
          <div className="h-32 bg-slate-800 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-6">
        <button onClick={() => navigate('/users')} className="flex items-center gap-2 text-slate-400 hover:text-slate-200 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Users
        </button>
        <div className="p-8 bg-red-900/20 border border-red-800/50 rounded-lg text-center">
          <p className="text-red-400">{error || 'User not found'}</p>
          <button onClick={fetchUser} className="mt-3 text-sm text-red-300 hover:text-red-200 underline">Retry</button>
        </div>
      </div>
    );
  }

  const roleDisplay = (user.roleCode || 'TARK-USER').replace('TARK-', '');

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button onClick={() => navigate('/users')} className="flex items-center gap-2 text-slate-400 hover:text-slate-200 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Users
      </button>

      {/* User header card */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-xl font-bold">
              {user.firstName?.[0]}{user.lastName?.[0]}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {user.firstName} {user.lastName}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                {user.profileName && (
                  <span className="text-slate-400">@{user.profileName}</span>
                )}
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                  user.roleCode === 'TARK-ADMIN'
                    ? 'bg-purple-500/20 text-purple-400'
                    : user.roleCode === 'TARK-MODERATOR'
                    ? 'bg-amber-500/20 text-amber-400'
                    : 'bg-slate-500/20 text-slate-400'
                }`}>
                  <Shield className="h-3 w-3" />
                  {roleDisplay}
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  user.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {user.active ? 'Active' : 'Suspended'}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-1">ID: {user.id} | {user.email}</p>
            </div>
          </div>

          {/* Risk badge */}
          {user.riskScore != null && user.riskLevel && (
            <div className={`flex flex-col items-center px-4 py-2 rounded-lg ${
              user.riskLevel === 'CRITICAL' ? 'bg-red-900/30 border border-red-700/50' :
              user.riskLevel === 'HIGH' ? 'bg-orange-900/30 border border-orange-700/50' :
              user.riskLevel === 'MEDIUM' ? 'bg-amber-900/30 border border-amber-700/50' :
              'bg-green-900/30 border border-green-700/50'
            }`}>
              <span className={`text-xs font-medium ${
                user.riskLevel === 'CRITICAL' ? 'text-red-400' :
                user.riskLevel === 'HIGH' ? 'text-orange-400' :
                user.riskLevel === 'MEDIUM' ? 'text-amber-400' :
                'text-green-400'
              }`}>
                {user.riskLevel} RISK
              </span>
              <span className="text-2xl font-bold text-white">{user.riskScore}</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-700">
        <nav className="flex gap-1 -mb-px">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? tab.id === 'danger'
                      ? 'border-red-500 text-red-400'
                      : 'border-cyan-500 text-cyan-400'
                    : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'profile' && (
          <UserProfileTab user={user} isEditing={isEditing} isSaving={isSaving} onEdit={() => setIsEditing(true)} onCancel={() => setIsEditing(false)} onSave={handleSave} onHardDelete={() => setShowHardDelete(true)} />
        )}
        {activeTab === 'account' && (
          <UserAccountTab user={user} isEditing={isEditing} isSaving={isSaving} onEdit={() => setIsEditing(true)} onCancel={() => setIsEditing(false)} onSave={handleSave} onHardDelete={() => setShowHardDelete(true)} />
        )}
        {activeTab === 'rankings' && (
          <UserRankingsTab user={user} isEditing={isEditing} isSaving={isSaving} onEdit={() => setIsEditing(true)} onCancel={() => setIsEditing(false)} onSave={handleSave} onHardDelete={() => setShowHardDelete(true)} />
        )}
        {activeTab === 'risk' && (
          <UserRiskTab user={user} />
        )}
        {activeTab === 'danger' && (
          <UserDangerZoneTab user={user} onRefresh={fetchUser} />
        )}
      </div>

      {showHardDelete && (
        <HardDeleteModal
          user={user}
          onClose={() => setShowHardDelete(false)}
          onSuccess={() => {
            setShowHardDelete(false);
            navigate('/users');
          }}
        />
      )}
    </div>
  );
}
